import { type PassageLocator } from "@client/lib/deepLinks/passage";
import { funderReviewHref } from "./useFunderWorkspaceRouteState";

// Passage-link locators for the funder-workspace surfaces. Each owns its
// surface's invariants the way `fieldLocator` does for proposal fields:
// scope-id format (`<kind>:<id>`, ids are nanoid so `:` can't collide) and
// the canonical URL. Every locator pins `path` explicitly — workspace content
// is reachable from several routes (rails remember state across tab flips;
// the eval modal also renders on the Decide tab), and a link must point at
// the one canonical Review-rail URL that navigates to the content.

/**
 * The round-level synthesis summary card on the Review Overview rail.
 * Regenerable AI synthesis, so — like deep research — the anchor leans on the
 * quote validator for durability across regenerations: land when the quoted
 * text survives, honestly miss when it doesn't. The pyramid renders only the
 * tiers expanded so far, so the pyramid's own revealer (keyed on this scope)
 * expands fully on landing.
 */
export function roundSummaryLocator(roundId: string): PassageLocator {
  return {
    path: funderReviewHref(roundId, "overview"),
    params: {},
    scopeId: `round-summary:${roundId}`,
  };
}

/**
 * The round summary's cross-track divergences disclosure — a child scope
 * nested inside the card scope (closest enabled scope wins minting, like the
 * eval modal's note/summary inside the modal scope). Its own scope makes
 * divergence links immune to the pyramid's expansion state: the offset is
 * measured within the disclosure body alone, and a phrase duplicated in the
 * synthesis text is outside this scope's root, so the matcher can never bind
 * it. The body unmounts while closed, so landing on a closed disclosure is a
 * genuine miss and the revealer opens it.
 */
export function roundSummaryDivergencesLocator(
  roundId: string,
): PassageLocator {
  return {
    path: funderReviewHref(roundId, "overview"),
    params: {},
    scopeId: `round-summary-divergences:${roundId}`,
  };
}

/**
 * A track's synthesis summary card under its meeting video (tracks rail).
 * Same durability model as {@link roundSummaryLocator}. `track` is the
 * assignable-track key — it IS the rail item id in the URL, so (like turn
 * seconds) it's the documented exception to nanoid-only scope ids.
 */
export function trackSummaryLocator(
  roundId: string,
  track: string,
): PassageLocator {
  return {
    path: funderReviewHref(roundId, "tracks", track),
    params: {},
    scopeId: `track-summary:${track}`,
  };
}

/** Evaluator note summary for a proposal (orgs rail). */
export function proposalNoteSummaryLocator(
  roundId: string,
  proposalId: string,
): PassageLocator {
  return {
    path: funderReviewHref(roundId, "orgs", proposalId),
    params: {},
    scopeId: `pns:${proposalId}`,
  };
}

/** Recommender profile summary (recs rail). */
export function recommenderSummaryLocator(
  roundId: string,
  recommenderId: string,
  memberId: string,
): PassageLocator {
  return {
    path: funderReviewHref(roundId, "recs", recommenderId),
    params: {},
    // Scoped by memberId, not recommenderId: the summary artifact and its
    // render sites key on the member, while the URL keys on the recommender.
    scopeId: `rns:${memberId}`,
  };
}

/**
 * The Recommender Profile card as a whole unit (recs rail) — a landing-only
 * scope over the card, the same split as {@link recEvalModalLocator}: the
 * `rns:` scope above lives on the full-summary prose, which renders only when
 * there IS prose (a pyramid-only profile has none) and only while the
 * disclosure is open, so it can't serve as the card's destination. The To Do
 * rail's "Viewed profile summary" step lands here.
 *
 * Landing-only: the prose keeps mint ownership for text selections (closest
 * wrapper wins), so this scope adds a destination without adding a second
 * copy affordance for the same content.
 */
export function recommenderSummaryCardLocator(
  roundId: string,
  recommenderId: string,
  memberId: string,
): PassageLocator {
  return {
    path: funderReviewHref(roundId, "recs", recommenderId),
    params: {},
    // Member-keyed like `rns:` — same artifact, same render site.
    scopeId: `rns-card:${memberId}`,
  };
}

/** A recommender's general round notes (recs rail). */
export function generalRoundNotesLocator(
  roundId: string,
  recommenderId: string,
  memberId: string,
): PassageLocator {
  return {
    path: funderReviewHref(roundId, "recs", recommenderId),
    params: {},
    scopeId: `general-notes:${memberId}`,
  };
}

/**
 * A recommender's evaluation of one proposal, shown in the eval detail modal.
 * The `proposal` param is what reopens the modal on landing (the recs rail's
 * existing `?proposal=` auto-open), so the modal needs no revealer.
 *
 * The note text and the AI summary get DISTINCT scopes: the summary quotes
 * the note verbatim by construction, so under one shared scope a passage
 * minted from the note could land on the summary's quotation of it (landing
 * binds first-announced and fails over only on a quote miss).
 */
export function recEvalLocator(
  roundId: string,
  recommenderId: string,
  memberId: string,
  proposalId: string,
  section: "note" | "summary",
): PassageLocator {
  const kind = section === "note" ? "rec-eval" : "rec-eval-summary";
  return {
    path: funderReviewHref(roundId, "recs", recommenderId),
    params: { proposal: proposalId },
    scopeId: `${kind}:${memberId}:${proposalId}`,
  };
}

/**
 * The eval detail modal as a whole unit — the corner copy-link button's
 * OBJECT link. A separate landing-only scope over the modal body (not the
 * note/summary scopes above) because those sections are conditional: a
 * score-only evaluation renders neither, and a mint affordance must never
 * outlive its landing target. Same `?proposal=` reopen as the section
 * locators.
 */
export function recEvalModalLocator(
  roundId: string,
  recommenderId: string,
  memberId: string,
  proposalId: string,
): PassageLocator {
  return {
    path: funderReviewHref(roundId, "recs", recommenderId),
    params: { proposal: proposalId },
    scopeId: `rec-eval-modal:${memberId}:${proposalId}`,
  };
}

/**
 * One turn of a meeting transcript (tracks rail). The link's quote is the
 * turn's text (icon-minted, so it never spans turns); `video` selects the
 * meeting via the rail's existing param handling and `t` pre-seeks the
 * player to the turn.
 *
 * Scope is `transcript:<meetingId>:<seconds>` — per TURN, keyed on the turn's
 * rounded start second. Turns have no persistent id, but their start second
 * is intrinsic to the utterance (parsed from the transcript's own timestamps,
 * monotonic within a meeting), so it survives re-parses far better than an
 * array index. Keying the scope on it means the link resolves to exactly one
 * turn — a bare per-meeting scope would let an earlier turn that merely embeds
 * the quoted substring (short/repeated turns like "Yes.") win the match. The
 * quote still validates content and degrades to a no-op if the turn is edited
 * away.
 */
export function transcriptTurnLocator(
  roundId: string,
  track: string,
  meetingId: string,
  turnSeconds: number,
): PassageLocator {
  const seconds = Math.round(turnSeconds);
  return {
    path: funderReviewHref(roundId, "tracks", track),
    // Both selection params, deliberately: `transcript` selects the meeting
    // by unit id (works for transcript-only meetings, where `video` matches
    // nothing) AND defaults the pane to the transcript view; `video` + `t`
    // pre-seek the player on meetings that have a recording.
    params: {
      transcript: meetingId,
      video: meetingId,
      t: String(seconds),
    },
    scopeId: `transcript:${meetingId}:${seconds}`,
  };
}

/**
 * One AI chapter of a meeting, shown on the pane's Chapters tab (tracks
 * rail). OBJECT link only — chapter titles/summaries are short regenerable
 * AI text, so there is no passage anchor to keep durable; landing scrolls to
 * the chapter row and pulses it.
 *
 * Scope is `chapter:<meetingId>:<seconds>` — keyed on the chapter's start
 * second like transcript turns (chapters have no persistent id; their start
 * timestamp is the stable thing about them). If chapters regenerate with
 * shifted boundaries the scope honestly misses — but the link still carries
 * `t`, so the recipient lands on the meeting seeked to the old chapter start.
 */
export function chapterLocator(
  roundId: string,
  track: string,
  meetingId: string,
  chapterSeconds: number,
): PassageLocator {
  const seconds = Math.round(chapterSeconds);
  return {
    path: funderReviewHref(roundId, "tracks", track),
    // Same selection params as {@link transcriptTurnLocator}: `transcript`
    // selects the meeting by unit id (and covers transcript-only meetings),
    // `video` + `t` pre-seek the player where there is a recording. The
    // chapter scope in the hash overrides the `transcript` param's
    // transcript-view default (see OverviewPanel's view init).
    params: {
      transcript: meetingId,
      video: meetingId,
      t: String(seconds),
    },
    scopeId: `chapter:${meetingId}:${seconds}`,
  };
}

/**
 * One AI chapter of a TRACK-LESS meeting (Review Overview rail) — the same
 * split as {@link overviewTurnLocator}: curated units like the round-overview
 * video have no tracks-rail URL, so their chapter links land on Overview.
 */
export function overviewChapterLocator(
  roundId: string,
  meetingId: string,
  chapterSeconds: number,
): PassageLocator {
  const seconds = Math.round(chapterSeconds);
  return {
    path: funderReviewHref(roundId, "overview"),
    params: {
      transcript: meetingId,
      video: meetingId,
      t: String(seconds),
    },
    scopeId: `chapter:${meetingId}:${seconds}`,
  };
}

/**
 * One turn of a TRACK-LESS meeting's transcript, viewed on the Review
 * Overview rail — curated units like the round-overview video have no track,
 * so {@link transcriptTurnLocator}'s canonical tracks-rail URL doesn't exist
 * for them. Same scope shape (`transcript:<meetingId>:<seconds>`) and the
 * same selection params, which the Overview rail's player honors identically.
 */
export function overviewTurnLocator(
  roundId: string,
  meetingId: string,
  turnSeconds: number,
): PassageLocator {
  const seconds = Math.round(turnSeconds);
  return {
    path: funderReviewHref(roundId, "overview"),
    params: {
      transcript: meetingId,
      video: meetingId,
      t: String(seconds),
    },
    scopeId: `transcript:${meetingId}:${seconds}`,
  };
}

/**
 * A whole uploaded excerpt in the clip browser on a proposal or recommender
 * page (orgs/recs rail) — the unit the "Copy link" action under the excerpt's
 * player mints. OBJECT link only: the shareable thing is the clip itself, not
 * a passage of its regenerable AI summary. Landing selects the excerpt (the
 * `excerpt` param is the same one the Watch pills use) and pulses its summary
 * header. Staleness (excerpt deleted or moved off this page) is handled by
 * the clip browser's revealer, which claims `excerpt:` scopes uncondition-
 * ally and scrolls the reader to the clips player when nothing resolves —
 * NOT by `landing=video`: the workspace scroll policy defers to any
 * non-empty hash, so a `landing=` param can never act as a fallback for a
 * hash-carrying link. The param is still set for non-landing consumers (it
 * pre-marks the video section as the page's semantic target if the hash is
 * ever stripped, e.g. a manually edited URL).
 *
 * Scope is `excerpt:<videoId>` — the excerpt's own video id, a stable upload
 * identity (unlike extracted-clip keys, which lapse on re-extraction). It
 * coincidentally matches the browser's internal selection key
 * (`excerptClipKey`); that is convenience, not a contract — neither side may
 * assume the other's format.
 */
export function excerptLocator(
  roundId: string,
  rail: "orgs" | "recs",
  railItemId: string,
  videoId: string,
): PassageLocator {
  return {
    path: funderReviewHref(roundId, rail, railItemId),
    params: { excerpt: videoId, landing: "video" },
    scopeId: `excerpt:${videoId}`,
  };
}

/**
 * One turn of a transcript viewed in the clip browser on a proposal or
 * recommender page (orgs/recs rail) — extracted meeting clips and uploaded
 * excerpts alike. Same scope shape as {@link transcriptTurnLocator}
 * (`transcript:<unitId>:<seconds>`), but the link lands back where it was
 * minted: the rail item's page, with the clip containing the turn selected
 * (ClipsPlayer's revealer resolves the unit + second to a current clip, so
 * the link survives clip re-extraction as long as some clip still covers the
 * moment). `unitId` is the meeting transcript id for extracted clips or the
 * excerpt's video id for uploads; seconds are on that unit's own clock
 * (meeting-transcript seconds vs excerpt-relative seconds).
 */
export function clipsRailTurnLocator(
  roundId: string,
  rail: "orgs" | "recs",
  railItemId: string,
  unitId: string,
  turnSeconds: number,
): PassageLocator {
  const seconds = Math.round(turnSeconds);
  return {
    path: funderReviewHref(roundId, rail, railItemId),
    params: {},
    scopeId: `transcript:${unitId}:${seconds}`,
  };
}

/**
 * A recommender's evaluation of one proposal, shown in the expandable
 * per-proposal rows under the recommender page's MVF chart. Same content as
 * the eval modal, but its own scope kinds (`rec-eval-row` /
 * `rec-eval-row-summary`): both copies live on the same page, so a shared
 * scope would let a modal-minted link bind the accordion copy underneath the
 * open modal (the texts are identical, so the quote validator can't fail
 * over). Distinct scopes also give the link land-where-minted semantics — no
 * `proposal` param, so landing reopens the accordion row (via the list's
 * revealer) rather than the modal.
 */
export function recEvalRowLocator(
  roundId: string,
  recommenderId: string,
  memberId: string,
  proposalId: string,
  section: "note" | "summary",
): PassageLocator {
  const kind = section === "note" ? "rec-eval-row" : "rec-eval-row-summary";
  return {
    path: funderReviewHref(roundId, "recs", recommenderId),
    params: {},
    scopeId: `${kind}:${memberId}:${proposalId}`,
  };
}

/**
 * Parse a `rec-eval-row(-summary):<memberId>:<proposalId>` scope, returning
 * its section + proposalId when it belongs to `memberId` (the recommender in
 * view — the list is member-scoped), else null.
 */
export function parseRecEvalRowScope(
  scopeId: string,
  memberId: string,
): { section: "note" | "summary"; proposalId: string } | null {
  const parts = scopeId.split(":");

  if (parts.length !== 3 || parts[1] !== memberId) {
    return null;
  }

  if (parts[0] === "rec-eval-row") {
    return { section: "note", proposalId: parts[2]! };
  }

  if (parts[0] === "rec-eval-row-summary") {
    return { section: "summary", proposalId: parts[2]! };
  }

  return null;
}

/**
 * A recommender's written MVF justification (their evaluable note), shown in
 * the proposal's MVF comparison — RecommenderViewsPanel on the orgs rail,
 * where each recommender's value curve sits beside their reasoning. Distinct
 * scope from `rec-eval` (the eval modal shows the same note): a link minted
 * while comparing curves should land back in the comparison, not the modal.
 * The note is verbatim (the evaluable note), so the quote anchor is durable.
 */
export function mvfJustificationLocator(
  roundId: string,
  proposalId: string,
  memberId: string,
): PassageLocator {
  return {
    path: funderReviewHref(roundId, "orgs", proposalId),
    params: {},
    scopeId: `mvf-note:${memberId}:${proposalId}`,
  };
}

/**
 * The AI summary of a recommender's note in the same MVF-comparison card —
 * `mvf-note`'s twin, split for the same reason as `rec-eval-summary`: the
 * summary quotes the note verbatim, so one shared scope could land note
 * quotes on the summary's quotation.
 */
export function mvfJustificationSummaryLocator(
  roundId: string,
  proposalId: string,
  memberId: string,
): PassageLocator {
  return {
    path: funderReviewHref(roundId, "orgs", proposalId),
    params: {},
    scopeId: `mvf-note-summary:${memberId}:${proposalId}`,
  };
}

/** memberId from an `mvf-note:<memberId>:<proposalId>` scope for `proposalId`, else null. */
export function mvfJustificationMemberId(
  scopeId: string,
  proposalId: string,
): string | null {
  const parsed = parseMvfJustificationScope(scopeId, proposalId);
  return parsed?.section === "note" ? parsed.memberId : null;
}

/**
 * Parse an `mvf-note(-summary):<memberId>:<proposalId>` scope, returning its
 * section + memberId when it belongs to `proposalId` (the comparison is
 * proposal-scoped), else null.
 */
export function parseMvfJustificationScope(
  scopeId: string,
  proposalId: string,
): { section: "note" | "summary"; memberId: string } | null {
  const parts = scopeId.split(":");

  if (parts.length !== 3 || parts[2] !== proposalId) {
    return null;
  }

  if (parts[0] === "mvf-note") {
    return { section: "note", memberId: parts[1]! };
  }

  if (parts[0] === "mvf-note-summary") {
    return { section: "summary", memberId: parts[1]! };
  }

  return null;
}
